import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNavigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";

import qs from "qs";
import color from "color";
import { AnimatePresence, motion } from "framer-motion";
import { useWindowSize } from "react-use";

import RingBell from "../../assets/icons/RingBell.svg";
import Checkin from "../../assets/icons/CheckIn.svg";
import Checkout from "../../assets/icons/CheckOut.svg";
import Food from "../../assets/icons/Food.svg";
import Parcel from "../../assets/icons/Parcel.svg";
import Call from "../../assets/icons/Call.svg";
import NoAppointment from "../../assets/icons/NoAppointment.svg";
import Overview from "../../assets/icons/Overview.svg";
import Wifi from "../../assets/icons/Wifi.svg";

import Title from "../../components/Title";
import Bump from "../../components/Animations/Bump";
import Curve from "../../components/Animations/PageTransitions/Curve";
import SvgIcon from "../../components/SvgIcon";
import InstructionDialog from "../../components/Instruction/InstructionDialog";

// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";

import Logger from "../../lib/logger";
import { beepDrop } from "../../lib/scanFeedback";
import useIdleEffect from "../../bit/board/hooks/useIdleEffect";
import useQueryBoard, {
  useCommunicationProvider,
  useDigitalReceptionMetadata,
  useHidScannerSettings,
  useLayout,
  useReceptionistPhone,
  useReceptionistTeamsEmail,
  useTeamsVideoCallEnabled,
} from "../../lib/hooks/useQueryBoard";
import { useAutoScanInput } from "../../lib/hooks/useAutoScanInput";
import { useTeamsCallerTokenAndTargetTeamsUserIds } from "../../lib/hooks/useTeamsCallerTokenAndTargetTeamsUserIds";

import { useOfflineContext } from "../../context/Offline";
import { HomeInput } from "../../components/HomeInput";
import { useGlobalSoundTestShortcut } from "../../lib/hooks/useGlobalSoundTestShortcut";
import { SoundTestModal } from "../../components/SoundTestModal";
import { useSchedulesContext } from "../../bit/schedules";

const IDLE_RELOAD_MINUTES = 5;
const METERED_IDLE_RELOAD_MINUTES = 33;

const isDev = process.env.NODE_ENV === "development";

const SCAN_COOLDOWN_MS = 1500;

const WifiButton = styled(motion.button)(({ theme }) => ({
  position: "absolute",
  right: "3rem",
  bottom: "21rem",
  display: "flex",
  alignItems: "center",
  margin: "2rem",
  padding: "2.5rem 3rem",
  borderRadius: "0.5rem",
  backgroundColor: theme.palette.primary.main,
  zIndex: 100,

  svg: {
    fill: theme.palette.primary.contrastText,
    marginRight: "2rem",
  },

  h4: {
    color: theme.palette.primary.contrastText,
  },

  "&:active": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:disabled": {
    backgroundColor: theme.palette.grey[400],
  },

  "@media screen and (orientation: landscape)": {
    bottom: "14rem",
  },
}));

export const MUIHomeButtonGroup = styled("div")({
  maxWidth: "90.4rem",
  width: "96%",
  display: "flex",
  flexDirection: "column",
  padding: "4.8rem 0",

  "@media screen and (orientation: landscape)": {
    maxWidth: "180.8rem",
    flexDirection: "row",
    padding: "0",

    "> div": {
      width: "50%",
    },
  },
});

export const HomeGrid = styled(Grid)({
  height: "68%",
  margin: "3rem",
  alignItems: "stretch",

  "@media screen and (orientation: landscape)": {
    margin: "0",
    height: "80%",
  },
});

export const MUIHomeButton = styled(motion.button)(({ theme }) => ({
  display: "block",
  width: "40rem",
  margin: "3.2rem",
  padding: "4rem 1.5rem",
  borderRadius: "1rem",
  backgroundColor: theme.palette.primary.main,

  svg: {
    fill: theme.palette.primary.contrastText,
    marginBottom: "3.2rem",
  },

  h2: {
    color: theme.palette.primary.contrastText,
  },

  "&:active": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:disabled": {
    backgroundColor: color(theme.palette.primary.main).lighten(0.4).hex(),
  },

  "@media screen and (orientation: landscape)": {
    margin: "0 3.2rem",
  },
}));

export const MUIHomeSmallButton = styled(motion.button)(({ theme }) => ({
  margin: "2rem auto",
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "4rem 1rem",
  borderRadius: "1rem",
  backgroundColor: theme.palette.primary.main,
  width: "65%",

  svg: {
    fill: theme.palette.primary.contrastText,
    position: "absolute",
    left: "4rem",
  },

  h3: {
    color: theme.palette.primary.contrastText,
  },

  "&:active": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },

  "&:disabled": {
    backgroundColor: color(theme.palette.primary.main).lighten(0.4).hex(),
  },

  "@media screen and (orientation: landscape)": {
    width: "30%",
    margin: "0.5rem 3rem",
    maxWidth: "70rem",
  },
}));

const HomePage = () => {
  const navigate = useNavigate();

  const { width, height } = useWindowSize();

  const orientation = width < height ? "portrait" : "landscape";

  const { pathname } = useLocation();

  const mainRoute = pathname === "/";

  const { data, isLoading } = useQueryBoard();
  const { t } = useTranslation(["home"]);

  const { online, refreshUntilMark } = useOfflineContext();

  const inputRef = useRef<HTMLInputElement>(null);

  const {
    welcomeMessage,
    checkin,
    independentManualCheckin,
    integratedManualCheckin,
    checkout,
    manualCheckout,
    packageDelivery,
    foodDelivery,
    callReception,
    overview,
    wifi,
    wayfinding,
    multiWayfinding,
    bell,
  } = useLayout();

  const { isHidAutoScanEnabled, hidAutoScanType, isOnlyQrScannerMode } =
    useHidScannerSettings();

  const [instruction, setInstruction] = useState<string | null>(null);

  const { verticalButtonLayout, network } = useDigitalReceptionMetadata();

  const scanInputRef = useRef<HTMLInputElement>(null);
  const lastScanAtRef = useRef(0);

  useEffect(() => {
    if (isOnlyQrScannerMode) {
      scanInputRef.current?.focus();
    }
  }, [isOnlyQrScannerMode]);

  useEffect(() => {
    if (pathname === "/") {
      lastScanAtRef.current = 0;
    }
  }, [pathname]);

  const communicationProvider = useCommunicationProvider();
  const receptionistTeamsEmail = useReceptionistTeamsEmail();
  const receptionistPhone = useReceptionistPhone();

  const shouldFetchTeamsToken = useMemo(() => {
    return (
      communicationProvider === "teams" &&
      !callReception?.callMenu?.enabled &&
      !!receptionistTeamsEmail
    );
  }, [callReception, communicationProvider, receptionistTeamsEmail]);

  const { callerAcsToken, targetTeamsUserIds, isGroupCall, status } =
    useTeamsCallerTokenAndTargetTeamsUserIds({
      emails: receptionistTeamsEmail ? [receptionistTeamsEmail] : [],
      enabled: shouldFetchTeamsToken,
    });

  const welcomeMessageText = welcomeMessage?.text || t("home:welcomeMessage");
  const checkinText = checkin?.text || t("home:checkIn");
  const independentManualCheckinText =
    independentManualCheckin?.text || t("home:independentManualCheckIn");
  const checkoutText = checkout?.text || t("home:checkOut");
  const manualCheckoutText = manualCheckout?.text || t("home:manualCheckOut");
  const packageDeliveryText = packageDelivery?.text || t("home:parcelDelivery");
  const foodDeliveryText = foodDelivery?.text || t("home:foodDelivery");
  const callReceptionText = callReception?.text || t("home:callReception");
  const overviewText = overview?.text || t("home:overview");
  const wifiText = wifi?.text || "Wi-Fi";
  const wayfindingText = wayfinding?.text || "Wayfinding";

  const bellText = bell?.text || "Bell";

  const moreThan4Buttons =
    [
      checkin?.enabled,
      independentManualCheckin?.enabled,
      checkout?.enabled || manualCheckout?.enabled,
      packageDelivery?.enabled,
      foodDelivery?.enabled,
      callReception?.enabled,
      overview?.enabled,
      wayfinding?.enabled,
      multiWayfinding?.enabled,
      bell?.enabled,
    ].filter(Boolean).length > 4;

  // usePageTracking({
  //   page: "home",
  // });

  // const { trackEvent } = useEventTracking();

  const teamsVideoCallEnabled = useTeamsVideoCallEnabled();

  const { screensaver, paused } = useSchedulesContext();

  const sleep = screensaver && !paused;

  useIdleEffect(
    () => {
      // we check the pathname for a trailing slash
      // because of service worker scope issue
      // https://github.com/w3c/ServiceWorker/issues/1272
      //
      // if there is no trailing slash, we redirect the user
      if (window.location.pathname.endsWith("/")) {
        window.location.reload();
        return;
      }

      window.location.href = `${window.location.pathname}/`;
    },
    (network?.idleReloadMinutes ??
      (network?.metered ? METERED_IDLE_RELOAD_MINUTES : IDLE_RELOAD_MINUTES)) *
      60 *
      1000,
    !sleep,
  );

  useEffect(() => {
    if (isDev || pathname.includes("teams")) {
      return;
    }

    navigate(`${pathname}/`, { replace: true });
  }, []);

  const handleClickCallReception = useCallback(() => {
    if (
      callReception?.callMenu?.enabled &&
      callReception?.callMenu?.options?.length > 1
    ) {
      return navigate("/calling-menu", {
        replace: true,
      });
    }

    if (communicationProvider === "teams") {
      return navigate(
        `/teams-${teamsVideoCallEnabled ? "video" : "audio"}-${
          isGroupCall ? "group-" : ""
        }call?${qs.stringify({
          callerAcsToken,
          targetTeamsUserIds,
        })}`,
        {
          replace: true,
        },
      );
    }

    return navigate("/contact/reception/call", {
      replace: true,
    });
  }, [
    navigate,
    callReception,
    callerAcsToken,
    targetTeamsUserIds,
    communicationProvider,
  ]);

  const handleAutoScan = useCallback(
    (code: string) => {
      let pathname = `/scan/${code}`;

      if (hidAutoScanType === "CHECKIN") {
        pathname = `/scan/${code}/checkin`;
      } else if (hidAutoScanType === "CHECKOUT") {
        pathname = `/scan/${code}/checkout`;
      }

      navigate(pathname);
    },
    [hidAutoScanType, navigate],
  );

  useAutoScanInput({
    inputRef,
    enabled: isHidAutoScanEnabled,
    onScan: handleAutoScan,
    minLength: 4,
    maxDelay: 200,
  });

  const { open, closeModal } = useGlobalSoundTestShortcut(inputRef);

  refreshUntilMark("home-refresh", 2000);

  if (!data && !isLoading) {
    return (
      <Box className="d-flex align-items-center justify-content-center h-100">
        <Typography variant="h1">{t("home:unavailable")}</Typography>
      </Box>
    );
  }

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column h-100">
        <Title className="p-0 mx-3" disableAnimation={!mainRoute}>
          {welcomeMessage?.enabled && (
            <Typography variant="h1">{welcomeMessageText}</Typography>
          )}
        </Title>
        <HomeGrid
          container
          justifyContent="center"
          alignItems="center"
          rowGap={orientation === "landscape" ? 4 : 0}
        >
          {!(
            moreThan4Buttons ||
            verticalButtonLayout ||
            (width < 641 && height !== 598)
          ) ? (
            <>
              {checkin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });

                      if (integratedManualCheckin?.enabled) {
                        return navigate("/access/option/checkin", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkin", { replace: true });
                    }}
                  >
                    <Checkin width="10rem" />
                    <Typography variant="h2">{checkinText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {independentManualCheckin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });
                      return navigate("/access/checkin/manual", {
                        replace: true,
                      });
                    }}
                  >
                    {checkin?.enabled ? (
                      <NoAppointment width="10rem" />
                    ) : (
                      <Checkin width="10rem" />
                    )}
                    <Typography variant="h2">
                      {independentManualCheckinText}
                    </Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {(checkout?.enabled || manualCheckout?.enabled) && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkout" });
                      if (checkout?.enabled && manualCheckout?.enabled) {
                        return navigate("/access/option/checkout", {
                          replace: true,
                        });
                      }

                      if (manualCheckout?.enabled) {
                        return navigate("/access/checkout/manual", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkout", { replace: true });
                    }}
                  >
                    <Checkout width="10rem" />
                    <Typography variant="h2">
                      {checkout?.enabled ? checkoutText : manualCheckoutText}
                    </Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {packageDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "package" });

                      if (packageDelivery?.isCustom) {
                        return navigate("/custom-delivery/package", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/package", { replace: true });
                    }}
                  >
                    <Parcel width="10rem" />
                    <Typography variant="h2">{packageDeliveryText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {foodDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "food" });

                      if (foodDelivery?.isCustom) {
                        return navigate("/custom-delivery/food", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/food", { replace: true });
                    }}
                  >
                    <Food width="10rem" />
                    <Typography variant="h2">{foodDeliveryText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {overview?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      return navigate("/overview", {
                        replace: true,
                      });
                    }}
                  >
                    <Overview width="10rem" />
                    <Typography variant="h2">{overviewText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {callReception?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    disabled={
                      !online ||
                      ((!communicationProvider ||
                        communicationProvider === "twilio") &&
                        !callReception?.callMenu?.enabled &&
                        (!receptionistPhone ||
                          receptionistPhone.trim() === "")) ||
                      (communicationProvider === "teams" &&
                        !callReception?.callMenu?.enabled &&
                        status !== "success")
                    }
                    onClick={handleClickCallReception}
                  >
                    <Call width="10rem" />
                    <Typography variant="h2">{callReceptionText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {bell?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });
                      return navigate(
                        `/bell/${bell?.bellController?.id}?timeout=${bell?.bellController?.timeout}`,
                        {
                          replace: true,
                        },
                      );
                    }}
                  >
                    <RingBell width="10rem" />
                    <Typography variant="h2">{bellText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {wayfinding?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      if (wayfinding?.instruction) {
                        setInstruction(wayfinding.instruction);
                      }
                    }}
                  >
                    {wayfinding?.svg && (
                      <SvgIcon id={wayfinding.svg} width="10rem" />
                    )}
                    <Typography variant="h2">{wayfindingText}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
              {multiWayfinding?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeButton
                    onClick={() => {
                      navigate("/wayfinding", {
                        replace: true,
                      });
                    }}
                  >
                    {multiWayfinding?.svg && (
                      <SvgIcon id={multiWayfinding.svg} width="10rem" />
                    )}
                    <Typography variant="h2">{multiWayfinding.text}</Typography>
                  </MUIHomeButton>
                </Bump>
              )}
            </>
          ) : (
            <>
              {checkin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });

                      if (integratedManualCheckin?.enabled) {
                        return navigate("/access/option/checkin", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkin", { replace: true });
                    }}
                  >
                    <Checkin width="6.5rem" />
                    <Typography variant="h3">{checkinText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {independentManualCheckin?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkin" });
                      return navigate("/access/checkin/manual", {
                        replace: true,
                      });
                    }}
                  >
                    {checkin?.enabled ? (
                      <NoAppointment width="6.5rem" />
                    ) : (
                      <Checkin width="6.5rem" />
                    )}

                    <Typography variant="h3">
                      {independentManualCheckinText}
                    </Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {(checkout?.enabled || manualCheckout?.enabled) && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "checkout" });
                      if (checkout?.enabled && manualCheckout?.enabled) {
                        return navigate("/access/option/checkout", {
                          replace: true,
                        });
                      }

                      if (manualCheckout?.enabled) {
                        return navigate("/access/checkout/manual", {
                          replace: true,
                        });
                      }

                      return navigate("/access/checkout", { replace: true });
                    }}
                  >
                    <Checkout width="6.5rem" />
                    <Typography variant="h3">
                      {checkout?.enabled ? checkoutText : manualCheckoutText}
                    </Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {packageDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "package" });

                      if (packageDelivery?.isCustom) {
                        return navigate("/custom-delivery/package", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/package", { replace: true });
                    }}
                  >
                    <Parcel width="6.5rem" />
                    <Typography variant="h3">{packageDeliveryText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {foodDelivery?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      // trackEvent({ type: "home_button_click", value: "food" });

                      if (foodDelivery?.isCustom) {
                        return navigate("/custom-delivery/food", {
                          replace: true,
                        });
                      }

                      return navigate("/delivery/food", { replace: true });
                    }}
                  >
                    <Food width="6.5rem" />
                    <Typography variant="h3">{foodDeliveryText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {overview?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      return navigate("/overview", {
                        replace: true,
                      });
                    }}
                  >
                    <Overview width="6.5rem" />
                    <Typography variant="h3">{overviewText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {callReception?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    disabled={
                      !online ||
                      ((!communicationProvider ||
                        communicationProvider === "twilio") &&
                        !callReception?.callMenu?.enabled &&
                        (!receptionistPhone ||
                          receptionistPhone.trim() === "")) ||
                      (communicationProvider === "teams" &&
                        !callReception?.callMenu?.enabled &&
                        status !== "success")
                    }
                    onClick={handleClickCallReception}
                  >
                    <Call width="6.5rem" />
                    <Typography variant="h3">{callReceptionText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {bell?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      return navigate(
                        `/bell/${bell?.bellController?.id}?timeout=${bell?.bellController?.timeout}`,
                        {
                          replace: true,
                        },
                      );
                    }}
                  >
                    <RingBell width="6.5rem" />
                    <Typography variant="h3">{bellText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {wayfinding?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      if (wayfinding?.instruction) {
                        setInstruction(wayfinding.instruction);
                      }
                    }}
                  >
                    {wayfinding?.svg && (
                      <SvgIcon id={wayfinding.svg} width="6.5rem" />
                    )}
                    <Typography variant="h3">{wayfindingText}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
              {multiWayfinding?.enabled && (
                <Bump disabled={!mainRoute}>
                  <MUIHomeSmallButton
                    onClick={() => {
                      navigate("/wayfinding", {
                        replace: true,
                      });
                    }}
                  >
                    {multiWayfinding?.svg && (
                      <SvgIcon id={multiWayfinding.svg} width="6.5rem" />
                    )}
                    <Typography variant="h3">{multiWayfinding.text}</Typography>
                  </MUIHomeSmallButton>
                </Bump>
              )}
            </>
          )}
        </HomeGrid>
        {wifi?.enabled && (
          <Bump disabled={!mainRoute}>
            <WifiButton
              onClick={() => {
                navigate("/wifi", {
                  replace: true,
                });
              }}
            >
              <Wifi width="5.5rem" />
              <Typography variant="h4">{wifiText}</Typography>
            </WifiButton>
          </Bump>
        )}
      </div>
      <AnimatePresence mode="wait">
        {instruction && (
          <InstructionDialog
            instruction={instruction}
            onDismiss={() => {
              setInstruction(null);
            }}
          />
        )}
      </AnimatePresence>

      <SoundTestModal open={open} onClose={closeModal} />
      {isOnlyQrScannerMode ? (
        <input
          id="qr-scanner-input"
          ref={scanInputRef}
          type="text"
          onBlur={(e) => e.target.focus()}
          onKeyDown={(e) => {
            if (
              (e.key === "Enter" || e.key === "Tab") &&
              e.currentTarget.value.trim().length >= 4
            ) {
              e.preventDefault();

              const now = Date.now();
              const code = e.currentTarget.value.trim();
              const sinceLast = now - lastScanAtRef.current;

              if (sinceLast < SCAN_COOLDOWN_MS) {
                Logger.warn("[qr-scanner] scan dropped (cooldown)", {
                  code,
                  sinceLastMs: sinceLast,
                });
                beepDrop();
                e.currentTarget.value = "";
                return;
              }
              lastScanAtRef.current = now;

              Logger.info("[qr-scanner] scan accepted", { code });
              handleAutoScan(code);
              e.currentTarget.value = "";
            }
          }}
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "40rem",
            padding: "2rem 2.5rem",
            fontSize: "2.4rem",
            textAlign: "center",
            borderRadius: "1rem",
            border: "2px solid black",
            color: "black",
            outline: "none",
            zIndex: 100,
          }}
        />
      ) : (
        <HomeInput ref={inputRef} />
      )}
      <Outlet />
    </Curve>
  );
};

export default HomePage;
